import {
  auth,
  db,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  linkWithCredential,
  EmailAuthProvider,
  ref,
  set,
  get,
} from "../Firebase/firebase-config"

function normalizePhone(phone) {
  if (!phone) return "";
  const cleaned = phone.replace(/[^0-9]/g, ""); // keep only digits
  return cleaned.startsWith("91") ? cleaned.slice(2) : cleaned; // remove +91/91
}

class AuthService {
  // Validation methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  isValidPhone(phone) {
    const phoneRegex = /^\d{10}$/
    return phoneRegex.test(phone)
  }

  isStrongPassword(password) {
    const hasLetter = /[a-zA-Z]/.test(password)
    const hasNumber = /\d/.test(password)
    const validLength = password.length >= 6 && password.length <= 12
    return hasLetter && hasNumber && validLength
  }

  isValidGSTIN(gstin) {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
    return gstinRegex.test(gstin)
  }

  isValidPAN(pan) {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/
    return panRegex.test(pan)
  }

  // Setup reCAPTCHA - Clear previous instance
  setupRecaptcha(containerId) {
    // Clear any existing reCAPTCHA
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear()
      window.recaptchaVerifier = null
    }

    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: "normal",
      callback: (response) => {
      },
      "expired-callback": () => {
      },
    })
    return window.recaptchaVerifier
  }

  // Clear reCAPTCHA
  clearRecaptcha() {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear()
      window.recaptchaVerifier = null
    }
  }

  // Check if phone number exists in Firebase Auth
  async checkPhoneExists(phoneNumber) {
    try {
      // Try to get user by phone number
      const methods = await auth.fetchSignInMethodsForEmail(phoneNumber)
      return methods.length > 0
    } catch (error) {
      // If error, assume phone doesn't exist
      return false
    }
  }

  // Phone authentication
  async sendOTP(phoneNumber, recaptchaVerifier) {
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
      return confirmationResult
    } catch (error) {
      throw this.getAuthErrorMessage(error)
    }
  }

  async verifyOTP(confirmationResult, otp) {
    try {
      const result = await confirmationResult.confirm(otp)
      return result
    } catch (error) {
      throw this.getAuthErrorMessage(error)
    }
  }

  // Email authentication with user existence check
  async signInWithEmail(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)

      // Check if user profile exists in database
      const userExists = await this.checkUserExists(userCredential.user.uid)
      if (!userExists) {
        // Sign out the user if profile doesn't exist
        await auth.signOut()
        throw new Error("User profile not found. Please complete registration first.")
      }

      return userCredential
    } catch (error) {
      throw this.getAuthErrorMessage(error)
    }
  }

  // Phone login with user existence check
  async signInWithPhone(phoneNumber, recaptchaVerifier) {
    try {
      // First check if phone number exists in our database
      const phoneExists = await this.checkPhoneInDatabase(phoneNumber)
      if (!phoneExists) {
        throw new Error("Phone number not registered. Please sign up first.")
      }

      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
      return confirmationResult
    } catch (error) {
      throw this.getAuthErrorMessage(error)
    }
  }

  // Check if phone exists in database
  async checkPhoneInDatabase(phoneNumber) {
    try {
      // Query all users to find matching phone number
      const usersRef = ref(db, "profileDashboard")
      const snapshot = await get(usersRef)

      if (snapshot.exists()) {
        const users = snapshot.val()
        for (const userId in users) {
          const user = users[userId]
          if (user.phone && phoneNumber.includes(user.phone)) {
            return true
          }
        }
      }
      return false
    } catch (error) {
      console.error("Error checking phone in database:", error)
      return false
    }
  }

  async createUserWithEmail(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      return userCredential
    } catch (error) {
      throw this.getAuthErrorMessage(error)
    }
  }

  // Link email to phone account
  async linkEmailToPhoneAccount(email, password) {
    try {
      const credential = EmailAuthProvider.credential(email, password)
      const result = await linkWithCredential(auth.currentUser, credential)
      return result
    } catch (error) {
      throw this.getAuthErrorMessage(error)
    }
  }

  // Password reset
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email)
      return true
    } catch (error) {
      throw this.getAuthErrorMessage(error)
    }
  }

  // Check if user exists in database
  async checkUserExists(uid) {
    try {
      const userRef = ref(db, `profileDashboard/${uid}`)
      const snapshot = await get(userRef)
      return snapshot.exists()
    } catch (error) {
      console.error("Error checking user existence:", error)
      return false
    }
  }

  // Save user profile
 async saveUserProfile(userData) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user");

    const userRef = ref(db, `profileDashboard/${user.uid}`);

    await set(userRef, {
      gstin: userData.gstin || "",
      pan: userData.pan || "",
      company_name: userData.company_name || "",
      phone: userData.phone || "",
      email: userData.email || "",
      username: userData.username || "",
      type: userData.type || "main",      // <-- THIS IS WHAT YOU NEEDED
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    return true;
  } catch (error) {
    console.error("Error saving user profile:", error);
    throw error;
  }
}

 

  // ✅ lookup by scanning profileDashboard
  async getUidByPhone(phone) {
    const cleanPhone = normalizePhone(phone);
    const usersRef = ref(db, "profileDashboard");
    const snapshot = await get(usersRef);

    if (snapshot.exists()) {
      const users = snapshot.val();
      for (const uid in users) {
        const user = users[uid];
        if (user.phone && normalizePhone(user.phone) === cleanPhone) {
          return uid;
        }
      }
    }
    return null; // not found
  }


   async checkPhoneInDatabase(phone) {
    try {
      const cleanInput = this.normalizePhone(phone);
      const usersRef = ref(db, "profileDashboard");
      const snapshot = await get(usersRef);

      if (snapshot.exists()) {
        const users = snapshot.val();
        for (const uid in users) {
          const user = users[uid];
          if (user.phone && this.normalizePhone(user.phone) === cleanInput) {
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      console.error("Error checking phone in database:", error);
      return false;
    }
  }

  // Get auth error message
  getAuthErrorMessage(error) {
    switch (error.code) {
      case "auth/user-not-found":
        return "No account found with this email."
      case "auth/wrong-password":
        return "Incorrect password. Please try again."
      case "auth/invalid-email":
        return "Please enter a valid email address."
      case "auth/user-disabled":
        return "This account has been disabled."
      case "auth/too-many-requests":
        return "Too many failed attempts. Please wait and try again."
      case "auth/network-request-failed":
        return "Network error. Check your internet connection."
      case "auth/invalid-phone-number":
        return "Invalid phone number format."
      case "auth/invalid-verification-code":
        return "Invalid OTP. Please check and try again."
      case "auth/code-expired":
        return "OTP has expired. Please request a new one."
      case "auth/quota-exceeded":
        return "SMS quota exceeded. Please try again later."
      case "auth/session-expired":
        return "Session expired. Please start over."
      case "auth/credential-already-in-use":
        return "This email is already linked to another account."
      case "auth/provider-already-linked":
        return "Email is already linked to this account."
      default:
        return error.message || "An error occurred. Please try again."
    }
  }
}
export { normalizePhone }; 
export default new AuthService()
